import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, CircleNotch, LockKey, Timer } from "@phosphor-icons/react";

function PinInput({ value, onChange, disabled, testid }) {
  return (
    <input
      data-testid={testid}
      type="password"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern="[0-9]*"
      maxLength={6}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
      className="mt-1.5 w-full border border-border bg-background px-4 py-3 text-center text-2xl tracking-[0.6em] rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
    />
  );
}

export default function Unlock() {
  const [splash, setSplash] = useState(true);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockSeconds, setLockSeconds] = useState(0);
  const [resetMsg, setResetMsg] = useState("");
  const timerRef = useRef(null);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setSplash(false), 1200);
    return () => clearTimeout(t);
  }, []);

  // Server-side lock state (survives refresh / cleared frontend state / clock changes).
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/devices/pin/state");
        if (r.data?.locked) {
          setLockSeconds(r.data.retry_after_seconds || 0);
          setError(r.data.message || "");
        }
      } catch (_) { /* state stays unlocked-form */ }
    })();
  }, []);

  useEffect(() => {
    if (lockSeconds <= 0) return undefined;
    timerRef.current = setInterval(() => setLockSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [lockSeconds > 0]);

  const countdown = useMemo(() => {
    const m = Math.floor(lockSeconds / 60);
    const s = lockSeconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }, [lockSeconds]);

  const submit = async (e) => {
    e.preventDefault();
    if (pin.length !== 6 || busy || lockSeconds > 0) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/devices/pin/unlock", { pin });
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail || "Please try again.";
      setError(typeof d === "string" ? d : "Please try again.");
      setPin("");
      if (err?.response?.status === 423) {
        const ra = parseInt(err?.response?.headers?.["retry-after"] || "0", 10);
        setLockSeconds(ra > 0 ? ra : 1800);
      }
      setBusy(false);
    }
  };

  const requestReset = async () => {
    try {
      const r = await api.post("/devices/pin/reset-request");
      setResetMsg(r.data?.message || "If this mobile access is eligible for reset, instructions have been sent to the registered email address.");
    } catch (_) {
      setResetMsg("If this mobile access is eligible for reset, instructions have been sent to the registered email address.");
    }
  };

  if (splash) {
    return (
      <div data-testid="mobile-splash" className="min-h-screen bg-primary flex flex-col items-center justify-center gap-4">
        <div className="h-16 w-16 bg-white rounded flex items-center justify-center">
          <ShieldCheck size={38} weight="fill" className="text-primary" />
        </div>
        <p className="font-display font-extrabold tracking-tight text-white text-xl">Rotary GOV Report Builder</p>
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/70">District 3192</p>
      </div>
    );
  }

  const locked = lockSeconds > 0;
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="unlock-page">
      <div className="max-w-sm w-full border border-border bg-surface rounded-md p-8 text-center">
        <div className="h-10 w-10 bg-primary rounded flex items-center justify-center mx-auto">
          <ShieldCheck size={22} weight="fill" className="text-white" />
        </div>
        <h1 className="mt-4 font-display font-extrabold tracking-tighter text-2xl" data-testid="unlock-title">
          Unlock Rotary GOV Report Builder
        </h1>
        <p className="mt-2 t-support text-muted-foreground">
          Enter your six-digit Rotary Access PIN to continue.
        </p>
        <form onSubmit={submit} data-testid="unlock-form">
          <PinInput value={pin} onChange={setPin} disabled={locked} testid="unlock-pin-input" />
          {error && <p data-testid="unlock-error" className="mt-3 text-sm text-red-700">{error}</p>}
          {locked && (
            <p data-testid="unlock-countdown" className="mt-2 text-sm text-amber-700 flex items-center justify-center gap-2">
              <Timer size={16} weight="duotone" /> Next attempt allowed in {countdown}
            </p>
          )}
          <button
            data-testid="unlock-submit-button" type="submit" disabled={busy || locked || pin.length !== 6}
            className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {busy ? <CircleNotch size={18} className="animate-spin" weight="bold" /> : <LockKey size={18} weight="bold" />}
            Unlock
          </button>
        </form>
        <button data-testid="pin-reset-request-button" onClick={requestReset}
          className="mt-4 text-sm text-primary hover:underline">
          Reset PIN
        </button>
        {resetMsg && <p data-testid="pin-reset-neutral" className="mt-2 text-xs text-muted-foreground">{resetMsg}</p>}
      </div>
    </div>
  );
}
